import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2 } from '@ui/Typography'

import { BankAccount } from '@model/BankAccount'

import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { MobileCard, MobileCardList } from '@shared/components/MobileCard/MobileCard'

import { BankBadgeStatus } from './BankBadgeStatus'

import styles from './BankAccountsCard.module.scss'

interface BankAccountsCardProps {
  bankAccounts?: BankAccount[]
}

export const BankAccountsCard: React.FC<BankAccountsCardProps> = ({ bankAccounts }) => {
  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()
  const [expandedRow, setExpandedRow] = useState('')

  const handleSetExpandedRow = (id: string) => {
    if (id && id !== expandedRow) {
      scrollIntoView([`bank-account-${id}`])
    }
    setExpandedRow(id === expandedRow ? '' : id)
  }

  return (
    <MobileCardList>
      {bankAccounts?.map((bank) => (
        <MobileCard className={styles.card} id={`bank-account-${bank.id}`}>
          <div className={styles.cardHead} onClick={() => handleSetExpandedRow(bank.id)}>
            <div className={styles.title}>
              <Body2 className={styles.name} medium>
                <SensitiveData>{bank.accountName}</SensitiveData>
              </Body2>
            </div>
            <div className={styles.subtitle}>
              <div className='has-cursor-pointer'>
                <span>
                  <BankBadgeStatus {...bank.status} />
                </span>
              </div>
            </div>
          </div>
          {expandedRow === bank.id && (
            <Fragment>
              <ExpandedField label={t('IBAN/Account Number')} value={bank.bankAccount} />
              <ExpandedField label={t('Bank Name')} value={bank.bankName} />
              <ExpandedField label={t('Bank Address')} value={bank.bankAddress} />
              <ExpandedField label={t('Swift')} value={bank.bankCode} />
              <ExpandedField label={t('Sort Code')} value={bank.bankCodeSort} />
              <ExpandedField label={t('Documents')} value={bank.document?.name} />
            </Fragment>
          )}
        </MobileCard>
      ))}
    </MobileCardList>
  )
}

export const ExpandedField: React.FC<{
  label: string
  value?: string
  remove?: () => void
}> = ({ label, value, remove }) => {
  if (!value && !remove) {
    return null
  }

  if (remove) {
    return (
      <div className={styles.subtitle}>
        <Body2 className={styles.remove}>{label}</Body2>
      </div>
    )
  }

  return (
    <div className={styles.subtitle}>
      <span className={styles.label}>{label}</span>
      <Body2 className={styles.value} medium>
        <SensitiveData>{value}</SensitiveData>
      </Body2>
    </div>
  )
}
